import {captureException} from '@sentry/browser';

import {assert} from 'Asserts';
import {warn} from 'util/Logging';

const DATA_CHANNEL_NAME = 'oai-events';
const MICROPHONE_LEVEL_SAMPLE_INTERVAL_MS = 50;
const MICROPHONE_LEVEL_NOISE_FLOOR = 0.01;
const MICROPHONE_LEVEL_FULL_SCALE_RMS = 0.12;

class MicrophoneLevelMonitor {
  private readonly analyser: AnalyserNode;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly samples: Float32Array<ArrayBuffer>;
  private timerId: number | null = null;
  private smoothedLevel = 0;

  constructor(
    microphoneStream: MediaStream,
    private readonly audioContext: AudioContext,
    private readonly levelListener: (level: number) => void
  ) {
    this.source = this.audioContext.createMediaStreamSource(microphoneStream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 512;
    this.samples = new Float32Array(this.analyser.fftSize);
    this.source.connect(this.analyser);
    this.sample();
  }

  private sample = (): void => {
    this.analyser.getFloatTimeDomainData(this.samples);
    const sumOfSquares = this.samples.reduce((sum, value) => sum + value * value, 0);
    const rms = Math.sqrt(sumOfSquares / this.samples.length);
    const normalizedLevel = Math.max(
      0,
      Math.min(
        1,
        (rms - MICROPHONE_LEVEL_NOISE_FLOOR) /
          (MICROPHONE_LEVEL_FULL_SCALE_RMS - MICROPHONE_LEVEL_NOISE_FLOOR)
      )
    );
    // React quickly to speech while letting the cloud settle instead of flickering between samples.
    this.smoothedLevel = Math.max(normalizedLevel, this.smoothedLevel * 0.72);
    this.levelListener(this.smoothedLevel);
    this.timerId = window.setTimeout(this.sample, MICROPHONE_LEVEL_SAMPLE_INTERVAL_MS);
  };

  async close(): Promise<void> {
    if (this.timerId != null) {
      window.clearTimeout(this.timerId);
    }
    this.levelListener(0);
    this.source.disconnect();
    await this.audioContext.close();
  }
}

/**
 * WebRTC session manager for realtime voice communication with an LLM.
 *
 * The initial target is the OpenAI Realtime API, see docs and code samples here:
 * https://platform.openai.com/docs/guides/realtime-webrtc
 *
 * This class should encapsulate the DOM-specific components, including audio playback and microphone
 * input. It is mocked for testing, so also should isolate anything DOM-related and avoid other business
 * logic.
 *
 * Usage:
 * const session = await WebRTCSession.create();
 * const sdpOffer = await session.createSDPOffer();
 * await session.startSessionWithSDPAnswer([get SDP answer based on offer]);
 * ...
 * session.close();
 */
export default class WebRTCSession {
  private wakeLock: WakeLockSentinel | null = null;

  private constructor(
    private readonly peerConnection: RTCPeerConnection,
    private readonly dataChannel: RTCDataChannel,
    private readonly microphoneStream: MediaStream,
    private readonly audioElement: HTMLAudioElement,
    private readonly microphoneLevelMonitor: MicrophoneLevelMonitor | null
  ) {}

  /**
   * Create and initialize a new WebRTC session.
   *
   * Accepts a data event callback that takes the parsed JSON data of each event.
   */
  static async create(
    dataEventListener: (event: unknown) => Promise<void> | void,
    connectionStateListener: (state: RTCPeerConnectionState) => void = () => {},
    microphoneLevelListener?: (level: number) => void
  ): Promise<WebRTCSession> {
    // Resume while the start-button user activation is still synchronous. Waiting until after
    // getUserMedia resolves can leave the analyser suspended behind browser autoplay policy.
    const microphoneLevelAudioContext = microphoneLevelListener == null ? null : new window.AudioContext();
    microphoneLevelAudioContext?.resume().catch(captureException);
    const peerConnection = new window.RTCPeerConnection();
    peerConnection.addEventListener('connectionstatechange', () => {
      connectionStateListener(peerConnection.connectionState);
    });
    const dataChannel = peerConnection.createDataChannel(DATA_CHANNEL_NAME);
    dataChannel.addEventListener('message', (e) => {
      const data = this.parseDataEvent(e);
      if (data != null) {
        Promise.resolve()
          .then(() => dataEventListener(data))
          .catch(captureException);
      }
    });

    const audioElement = document.createElement('audio');
    audioElement.autoplay = true;
    peerConnection.ontrack = (event) => {
      audioElement.srcObject = event.streams[0];
    };

    let microphoneStream: MediaStream;
    try {
      microphoneStream = await window.navigator.mediaDevices.getUserMedia({audio: true});
    } catch (error) {
      await microphoneLevelAudioContext?.close();
      throw error;
    }
    for (const track of microphoneStream.getTracks()) {
      peerConnection.addTrack(track);
    }
    const microphoneLevelMonitor =
      microphoneLevelListener == null || microphoneLevelAudioContext == null
        ? null
        : new MicrophoneLevelMonitor(microphoneStream, microphoneLevelAudioContext, microphoneLevelListener);

    return new WebRTCSession(
      peerConnection,
      dataChannel,
      microphoneStream,
      audioElement,
      microphoneLevelMonitor
    );
  }

  /**
   * Parse the JSON payload out of a raw data event.
   *
   * We also log all events to the console as an alternative to Chrome DevTools Network tab, which
   * currently does not show data channel events.
   */
  static parseDataEvent(event: unknown): unknown {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = JSON.parse((event as any).data);
      // eslint-disable-next-line no-console
      console.log('[WebRTCSession] Received data event', data);
      return data;
    } catch (e) {
      if (!(e instanceof SyntaxError)) {
        throw e;
      }
      warn('[WebRTCSession] Failed to parse data event.');
      // eslint-disable-next-line no-console
      console.log('Event details:', event);
      return null;
    }
  }

  /**
   * Send the given event object (must be JSON-serializable) via the data channel.
   *
   * We also log all events to the console as an alternative to Chrome DevTools Network tab, which
   * currently does not show data channel events.
   */
  sendDataEvent(event: unknown): void {
    // eslint-disable-next-line no-console
    console.log('[WebRTCSession] Sent data event', event);
    this.dataChannel.send(JSON.stringify(event));
  }

  /**
   * Create an SDP offer for this session.
   */
  async createSDPOffer(): Promise<string> {
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    const sdp = offer.sdp;
    assert(sdp != null, 'Unexpectedly received null SDP');
    return sdp;
  }

  /**
   * Start the session based on the SDP answer received from the server.
   */
  async startSessionWithSDPAnswer(sdpAnswer: string): Promise<void> {
    await this.peerConnection.setRemoteDescription({
      type: 'answer',
      sdp: sdpAnswer,
    });
    await this.acquireWakeLock();
  }

  /**
   * Acquire a wake lock to keep the screen awake during the session.
   */
  private async acquireWakeLock(): Promise<void> {
    try {
      this.wakeLock = await window.navigator.wakeLock.request('screen');
    } catch (err) {
      if (err instanceof DOMException) {
        // The wake lock may be refused, e.g. on low battery. Ignore the error in that case.
        warn(`Failed to acquire wake lock: ${err.message}`);
      } else {
        throw err;
      }
    }
  }

  /**
   * Close the session and clean up all resources.
   */
  async close(): Promise<void> {
    this.peerConnection.close();
    this.dataChannel.close();
    for (const track of this.microphoneStream.getTracks()) {
      track.stop();
    }
    await this.microphoneLevelMonitor?.close();
    this.audioElement.pause();
    await this.wakeLock?.release();
  }
}
